import { supabase } from "@/lib/supabase";

/**
 * Base URL for the api-server routes that replaced the Supabase Edge Functions.
 *
 * Every function is served by the self-hosted api-server (VITE_API_URL). The
 * per-function VITE_MIGRATED_FUNCTIONS allow-list that let a name be routed back
 * to Supabase Edge during the cutover was retired with CR-71: the Edge Functions
 * are deleted from every environment, so that route would only 404. When
 * VITE_API_URL is unset (local), URLs fall back to the Supabase host so the app
 * keeps working against a local stack that fronts the api-server.
 */
const API_URL = import.meta.env.VITE_API_URL as string | undefined;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

/**
 * Absolute URL for an api-server function route. `name` may carry a query string
 * (e.g. "oauth-exchange?service=github").
 */
export function functionUrl(name: string): string {
  // `||`, not `??`: an unset GitHub Actions var renders as "" at build time.
  return `${API_URL || SUPABASE_URL}/functions/v1/${name}`;
}

/** Standard auth headers for a function call (Bearer JWT + Supabase apikey). */
export async function functionHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  // Header names and the auth scheme are protocol data, not copy: extracted, a
  // translated "Content-Type" makes fetch() throw "Invalid name" under fr/de.
  // @wc-ignore
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${session?.access_token ?? ""}`,
    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  };
}

export interface InvokeError {
  message: string;
  status?: number;
  /** Parsed JSON error body, when present. */
  body?: unknown;
}

export interface InvokeResult<T> {
  data: T | null;
  error: InvokeError | null;
}

/**
 * Drop-in replacement for `supabase.functions.invoke()` that honours `functionUrl()`.
 * Mirrors invoke()'s `{ data, error }` shape. Use `method: "GET"` for query-param GETs.
 */
export async function invokeFunction<T>(
  name: string,
  opts: { body?: unknown; method?: string } = {},
): Promise<InvokeResult<T>> {
  const method = opts.method ?? "POST";
  try {
    const res = await fetch(functionUrl(name), {
      method,
      headers: await functionHeaders(),
      body: method === "GET" || opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
    const text = await res.text();
    const parsed: unknown = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const p = parsed as { error?: string; msg?: string } | null;
      return {
        data: null,
        error: {
          message: p?.error ?? p?.msg ?? res.statusText,
          status: res.status,
          body: parsed,
        },
      };
    }
    return { data: parsed as T, error: null };
  } catch (err) {
    return {
      data: null,
      error: { message: err instanceof Error ? err.message : "Request failed" },
    };
  }
}
